import { combineReducers, Middleware } from 'redux';
import { configureStore as configureStoreToolkit, Tuple } from '@reduxjs/toolkit';
import { ThunkAction, ThunkMiddleware, withExtraArgument } from 'redux-thunk';
import disclaimer from './Disclaimer/disclaimerReducer';
import boardServices from './ServiceBoardService/serviceBoardServiceReducer';
import learnMore from './LearnMoreItem/learnMoreReducer';
import recommendations from './RecommendedServices/recommendedServicesReducer';
import applicationContext from './AppContext/applicationContextReducer';
import language from './Language/languageReducer';
import authenticatedUserContacts from './AuthenticatedUserContact/authenticatedUserContactReducer';
import introTour from './IntroTour/introTourReducer';
import { SgmSearchReducer } from './SgmSearch/sgmSearchReducer';
import consent from './Consent/consentReducer';
import bookmark from './Bookmark/bookmarkReducer';
import { CategoriesReducer } from './Categories/categoriesReducer';
import essentialService from './EssentialServices/essentialServicesReducer';
import externalService from './ExternalService/externalServiceReducer';
import highlightLayout from './HighlightLayout/highlightLayoutReducer';
import myAccountSetting from './MyAccountSetting/myAccountSettingReducer';
import myAccountPersonalInformation from './MyAccountPersonalInformation/myAccountPersonalInformationReducer';
import signature from './Signature/signatureReducer';
import { NormalizrReducer } from './Normalizr/NormalizrReducer';
import { ActionType } from './ActionType';
import { NotificationReducer } from './Notification/notificationReducer';
import { TFunction } from 'i18next';
import { WidgetReducer } from './Widget/widgetReducer';
import { useDispatch, useSelector } from 'react-redux';

export type Dependencies = {
    translate: TFunction;
};

const lastActionType = (_state: string, action: ActionType) => {
    return action.type;
};

const reducers = {
    disclaimer,
    lastActionType,
    applicationContext,
    language,
    boardServices,
    learnMore,
    recommendations,
    authenticatedUserContacts,
    search: SgmSearchReducer,
    bookmark,
    essentialService,
    introTour,
    consent,
    category: CategoriesReducer,
    externalService,
    highlightLayout,
    myAccountSetting,
    myAccountPersonalInformation,
    signature,
    entities: NormalizrReducer,
    notification: NotificationReducer,
    widget: WidgetReducer,
};

export type AppState = {
    [P in keyof typeof reducers]: ReturnType<typeof reducers[P]>;
};

export type ThunkResult<R = void> = ThunkAction<
    R,
    AppState,
    Partial<Dependencies>,
    ActionType
>;

export type ApiRequest<T = undefined> = {
    readonly state: 'neverFetched' | 'isFetching' | 'valid' | 'outdated' | 'error';
    readonly data?: T;
};

export type Reducer<S = any, A = ActionType | { type: 'TEST' }> = (
    state: S | undefined,
    action: A
) => S

export const configureStore = (
    dependencies: Partial<Dependencies>,
    middlewares: Middleware[] = [],
) => configureStoreToolkit({
    reducer: combineReducers(reducers),
    middleware: () => new Tuple(
        withExtraArgument(dependencies) as ThunkMiddleware<
            AppState,
            ActionType,
            Partial<Dependencies>
        >,
        ...middlewares,
    ),
});

export type AppStore = ReturnType<typeof configureStore>;
export type AppDispatch = AppStore['dispatch'];

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<AppState>();